import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { reversedRoutes, mainRoutesCamelCase } from '../helpers/constants';
import { connect } from 'react-redux';
import * as Store from '@apply/store';
export interface BackButtonProps {
    disabled?: boolean;
    // used when additional usage specific processing is required
    onClick?: () => Promise<boolean>;
}

const formConstant = Store.FormsStore.Types.formConstant;

export const BrowserBackButton: React.FC<BackButtonProps> = ({ disabled = false, onClick }) => {
    const navigate = useNavigate();

    const handleClick = async () => {
        // will await the handler and if fails, not execute the go back
        if (onClick) {
            const result = await onClick();
            if (!result) {
                return;
            }
        }
        navigate(-1);
    };

    return (
        <button
            onClick={handleClick}
            disabled={disabled}
            className='button'
        >
            Back
        </button>
    );
};

const checkOnlineForm = (forms, propertyId, formName) => {
    const filteredForms = forms.filter((form) => form.propertyGuid === propertyId);
    const form = filteredForms.find((form) => form.type === formConstant.displayName[formName]);

    return form;
};
const checkIfAnotherProperty = (items, propertyId, index) => {
    const propertyIndex = items.findIndex((property) => property.guid === propertyId);
    if (propertyIndex > 0) {
        const newPropertyGuid = items[propertyIndex - 1].guid;
        return {
            newPropertyGuid,
            newPropertyIndex: reversedRoutes.indexOf('review'),
        };
    }
    return { newPropertyGuid: undefined, newPropertyIndex: index };
};

// checkNextForm Worst Edge Case 1:
// Api returned a misspelled form type.
// The index would get returned of specSheet and a blank form would be shown,
// They could fill out this form and it is now filled out online.
// Or just click back and when they click next that form will not be remembered..

// checkNextForm Worst Edge Case 2:
// New form type - if this is not in the constants.
// That item will never be reached and the next form will show
// This is as intended and updating the mainRoutes  constant when interacting with React Router
// A developer should also update the mainRoutes in this file.

const checkNextForm = ({ navigate, formName, index, items, forms, propertyId, fixRoute, apiId, token }) => {
    // Base Case
    // If somehow the index iterated too far terminate function with a good length.
    if (index === reversedRoutes.length) {
        // Should never be reached
        return reversedRoutes.length - 1;
    }

    switch (formName) {
        case 'specSheet':
            if (checkOnlineForm(forms, propertyId, formName)) {
                return index;
            } else {
                const newIndex = index + 1;
                const newFormName = reversedRoutes[newIndex];
                const finalIndex = checkNextForm({
                    navigate,
                    formName: newFormName,
                    index: newIndex,
                    items,
                    forms,
                    propertyId,
                    fixRoute,
                    apiId,
                    token,
                });
                return finalIndex;
            }
        case 'budget':
        case 'landBudget':
        case 'fixFlipBudget':
            if (checkOnlineForm(forms, propertyId, formName)) {
                return index;
            } else {
                const newIndex = index + 1;
                const newFormName = reversedRoutes[newIndex];
                const finalIndex = checkNextForm({
                    navigate,
                    formName: newFormName,
                    index: newIndex,
                    items,
                    forms,
                    propertyId,
                    fixRoute,
                    apiId,
                    token,
                });
                return finalIndex;
            }
        case 'property':
            // No change here property page is straightforward.
            return index;
        case 'borrower': {
            const { newPropertyGuid, newPropertyIndex } = checkIfAnotherProperty(items, propertyId, index);
            if (newPropertyGuid) {
                navigatePage({
                    navigate,
                    fixRoute,
                    apiId,
                    items,
                    forms,
                    propertyId: newPropertyGuid,
                    index: newPropertyIndex,
                    token,
                });
                return -1; // abort ... exit condition.
                // we need to end this navigatePage function and fire a new navigatePage function.
            }
            return index;
        }
        default:
            return index;
    }
};

const navigatePage = ({ navigate, fixRoute, apiId, items, forms, propertyId, index = undefined, token = undefined }) => {
    const computedIndex = index ? index : reversedRoutes.indexOf(fixRoute);
    const newIndex = computedIndex + 1;
    const newFormName = reversedRoutes[newIndex];
    const finalIndex = checkNextForm({
        navigate,
        formName: newFormName,
        index: newIndex,
        items,
        forms,
        propertyId,
        fixRoute,
        apiId,
        token,
    });

    if (finalIndex !== -1) {
        const finalFormName = reversedRoutes[finalIndex];

        switch (finalFormName) {
            case 'appraisal':
                navigate(`/review/${apiId}/?pid=${propertyId ?? ''}${token ? '&token=' + token : ''}`);
                return;
            case 'review':
                navigate(`/review/${apiId}/?pid=${propertyId ?? ''}${token ? '&token=' + token : ''}`);
                return;
            case 'specSheet':
                navigate(`/specsheet/${apiId}/?pid=${propertyId ?? ''}${token ? '&token=' + token : ''}`);
                return;
            case 'budget':
                navigate(`/budget/${apiId}/?pid=${propertyId ?? ''}${token ? '&token=' + token : ''}`);
                return;
            case 'fixFlipBudget':
                navigate(`/fixFlipBudget/${apiId}/?pid=${propertyId ?? ''}${token ? '&token=' + token : ''}`);
                return;
            case 'landBudget':
                navigate(`/landBudget/${apiId}/?pid=${propertyId ?? ''}${token ? '&token=' + token : ''}`);
                return;
            case 'property':
                navigate(`/property/${apiId}/?pid=${propertyId ?? ''}${token ? '&token=' + token : ''}`);
                return;
            case 'borrower':
                navigate(`/borrower/${apiId}${token ? '?token=' + token : ''}`);
                return;
            case 'home':
                navigate(`/home`);
                return;
            default:
                alert('use browser back button');
                return;
        }
    }
};

const navigateNextPage = ({ navigate, fixRoute, apiId, items, forms, propertyId }) => {
    return; // this will be implemented in future.
    // finishLine({history, fixRoute, apiId, items, forms, propertyId, increment: false})
};

const navigatePreviousPage = ({ navigate, fixRoute, apiId, items, forms, propertyId, token }) => {
    // finishLine({history, fixRoute, apiId, items, forms, propertyId, increment: true})
    navigatePage({
        navigate,
        fixRoute,
        apiId,
        items,
        forms,
        propertyId,
        token,
    });
};

const goBack = ({ navigate, properties, apiId, propertyId, token }) => {
    const [route] = window.location.pathname.split('/').filter((x) => x);
    const fixRoute = mainRoutesCamelCase[route];
    const { items, forms } = properties;
    navigatePreviousPage({
        navigate,
        fixRoute,
        apiId,
        items,
        forms,
        propertyId,
        token,
    });
};
export const BackButton: React.FC<any> = ({ disabled = false, onClick, properties, borrower }) => {
    const navigate = useNavigate();
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const propertyId = params.get('pid') ?? properties?.items[properties.items.length - 1]?.guid;
    const token = params.get('token');

    const handleClick = async () => {
        const { apiId } = borrower;
        if (onClick) {
            const result = await onClick();
            if (!result) {
                return;
            }
        }
        goBack({ navigate, properties, apiId, propertyId, token });
    };

    return (
        <button
            onClick={handleClick}
            disabled={disabled}
            className='button'
        >
            Back
        </button>
    );
};
export const mapStateToProps = (state: Store.ApplyRootStateType) => ({
    properties: state.properties,
    borrower: state.borrower,
});
export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { dispatch: Store.ApplyDispatchType } = {
        dispatch,
    };
    return props;
};

export const BackButtonWithStore = connect(mapStateToProps, mapDispatchToProps)(BackButton);
